import { useMemo } from 'react';
import { useQuery, QueryResult } from '@apollo/client';
// Api
import { GET_PRICE_MARGINS, GET_STORE_PRICE_MARGINS } from 'api/margin/queries';
// Types
import { GetPriceMargins } from 'api/margin/types/GetPriceMargins';
import {
  GetStorePriceMargins,
  GetStorePriceMarginsVariables,
} from 'api/margin/types/GetStorePriceMargins';

type UseGetPriceMarginsOptions = {
  storeId?: string;
  skip?: boolean;
};

type UseGetPriceMarginsReturn = Omit<
  QueryResult<
    GetPriceMargins | GetStorePriceMargins,
    GetStorePriceMarginsVariables
  >,
  'data'
> & {
  data?: GetPriceMargins;
  getStorePriceMargins?: GetStorePriceMargins['getStorePriceMargins'];
};

export const useGetPriceMargins = (
  options: UseGetPriceMarginsOptions = {}
): UseGetPriceMarginsReturn => {
  const { storeId, skip } = options;

  const queryResult = useQuery<
    GetPriceMargins | GetStorePriceMargins,
    GetStorePriceMarginsVariables
  >(storeId ? GET_STORE_PRICE_MARGINS : GET_PRICE_MARGINS, {
    variables: storeId ? { storeId } : undefined,
    skip: skip ?? false,
    fetchPolicy: 'cache-and-network',
  });

  const normalizedData = useMemo(() => {
    const data = queryResult.data;
    if (!data) {
      return undefined;
    }

    if (storeId) {
      const storeMargins = (data as GetStorePriceMargins).getStorePriceMargins;
      return {
        getPriceMargins: {
          __typename: 'PriceMargins',
          amaMargin: storeMargins.amaMargin,
          merchMargin: storeMargins.merchMargin,
          streamMargin: storeMargins.streamMargin,
          tipMargin: storeMargins.tipMargin,
        },
      } as GetPriceMargins;
    }

    return data as GetPriceMargins;
  }, [queryResult.data, storeId]);

  const storePriceMargins = useMemo(() => {
    if (!storeId || !queryResult.data) {
      return undefined;
    }
    return (queryResult.data as GetStorePriceMargins)?.getStorePriceMargins;
  }, [storeId, queryResult.data]);

  return {
    ...queryResult,
    data: normalizedData,
    getStorePriceMargins: storePriceMargins,
  };
};
